import { Button } from "../Button/index.js";
import { Tooltip } from "../Tooltip/index.js";
import { Tab } from "../Tab/index.js";
import React, { useRef } from "react";
import { IconArrowLeft, IconArrowRight } from "@synthesis/shared-icons";
import { cn } from "@synthesis/shared-utils";
import { jsx, jsxs } from "react/jsx-runtime";
import { Tabs } from "@base-ui/react/tabs";
//#region src/TabGroup/index.tsx
const MAX_LINE_LENGTH_FOR_TOOLTIP_APPEARANCE = 210;
/**
* Группа вкладок с горизонтальной прокруткой.
* Дизайн - https://www.figma.com/file/uRPlsieHFpRpRsYDX2KV1Q/%D0%90%D1%81%D1%83%D0%B1%D0%B8%D0%BC.-%D0%A0%D0%96%D0%94-UI-Kit?node-id=402%3A5694
*/
const TabGroup = React.memo(({ tabs, activeKey, activationMode = "manual", onChange, className }) => {
	const listRef = useRef(null);
	const tabsRefs = useRef({});
	const defaultKey = tabs[0]?.key ?? "";
	const [internalActiveTab, setInternalActiveTab] = React.useState(activeKey ?? defaultKey);
	const activeTab = activeKey ?? internalActiveTab;
	const [isButtonsHide, setIsButtonsHide] = React.useState({
		left: true,
		right: true
	});
	const setButtonsByScroll = React.useCallback(() => {
		const list = listRef.current;
		if (!list) return;
		setIsButtonsHide({
			left: list.scrollLeft === 0,
			right: Math.trunc(list.scrollWidth) - 16 <= Math.trunc(list.offsetWidth + list.scrollLeft)
		});
	}, []);
	const scrollButtonsRafRef = useRef(void 0);
	const handleListScroll = React.useCallback(() => {
		if (scrollButtonsRafRef.current !== void 0) return;
		scrollButtonsRafRef.current = requestAnimationFrame(() => {
			scrollButtonsRafRef.current = void 0;
			setButtonsByScroll();
		});
	}, [setButtonsByScroll]);
	React.useEffect(() => {
		return () => {
			if (scrollButtonsRafRef.current !== void 0) cancelAnimationFrame(scrollButtonsRafRef.current);
		};
	}, []);
	const scrollRight = React.useCallback(() => {
		listRef.current?.scroll({
			left: (listRef.current?.scrollLeft ?? 0) + 300,
			behavior: "smooth"
		});
	}, []);
	const scrollLeft = React.useCallback(() => {
		listRef.current?.scroll({
			left: (listRef.current?.scrollLeft ?? 0) - 300,
			behavior: "smooth"
		});
	}, []);
	React.useLayoutEffect(() => {
		setButtonsByScroll();
	}, [setButtonsByScroll, tabs]);
	React.useLayoutEffect(() => {
		const list = listRef.current;
		if (!list) return;
		const resizeObserver = new ResizeObserver(() => setButtonsByScroll());
		resizeObserver.observe(list);
		return () => {
			resizeObserver.disconnect();
		};
	}, [setButtonsByScroll]);
	const onValueChange = (key) => {
		if (activeKey === void 0) setInternalActiveTab(key);
		onChange?.(key);
	};
	if (tabs.length === 0) return null;
	return /* @__PURE__ */ jsx(Tabs.Root, {
		value: String(activeTab),
		onValueChange,
		className: cn("min-w-0", className),
		children: /* @__PURE__ */ jsxs("div", {
			className: "relative w-full min-w-0",
			children: [
				!isButtonsHide.right ? /* @__PURE__ */ jsx("div", {
					className: "absolute right-0 top-1/2 z-10 -translate-y-1/2",
					children: /* @__PURE__ */ jsx(Button, {
						onClick: scrollRight,
						leftIcon: /* @__PURE__ */ jsx(IconArrowRight, { className: "size-4" }),
						theme: "elevated",
						size: "sm"
					})
				}) : null,
				!isButtonsHide.left ? /* @__PURE__ */ jsx("div", {
					className: "absolute left-0 top-1/2 z-10 -translate-y-1/2",
					children: /* @__PURE__ */ jsx(Button, {
						onClick: scrollLeft,
						leftIcon: /* @__PURE__ */ jsx(IconArrowLeft, { className: "size-4" }),
						theme: "elevated",
						size: "sm"
					})
				}) : null,
				/* @__PURE__ */ jsx(Tabs.List, {
					ref: listRef,
					activateOnFocus: activationMode === "automatic",
					onScroll: handleListScroll,
					className: cn("h-12 w-full min-w-0 overflow-x-auto overflow-y-hidden overscroll-x-contain", "[-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"),
					children: /* @__PURE__ */ jsx("div", {
						className: cn("relative flex h-full w-max min-w-full flex-nowrap items-center gap-6", "before:bg-neutrals-90 before:absolute before:bottom-[0.125rem] before:left-0 before:right-0 before:h-[0.1875rem] before:content-['']"),
						children: tabs.map(({ key, value, title, disabled = false }) => {
							const tabNode = /* @__PURE__ */ jsx(Tabs.Tab, {
								value,
								disabled,
								nativeButton: false,
								className: "shrink-0 grow-0 focus:outline-blue-300",
								render: (tabProps) => {
									const { ref, onClick, ...rest } = tabProps;
									return /* @__PURE__ */ jsx(Tab, {
										...rest,
										"data-cy": "tabGroup-tab",
										truncate: false,
										onClick: (e) => {
											if (disabled) return;
											onClick?.(e);
											onChange?.(value);
										},
										title,
										active: value === String(activeTab),
										className: cn(disabled && "text-type-light hover:text-type-light cursor-not-allowed opacity-60"),
										ref: (el) => {
											if (typeof ref === "function") ref(el);
											else if (ref && typeof ref === "object") ref.current = el;
											tabsRefs.current[key] = el ?? void 0;
										}
									});
								}
							}, key);
							const tabElement = tabsRefs.current[key];
							if (tabElement && tabElement.offsetWidth >= MAX_LINE_LENGTH_FOR_TOOLTIP_APPEARANCE) return /* @__PURE__ */ jsx(Tooltip, {
								description: title,
								arrowSide: "top",
								arrowAlign: "center",
								sideOffset: 0,
								children: tabNode
							}, `tooltip-${key}`);
							return tabNode;
						})
					})
				})
			]
		})
	});
});
TabGroup.displayName = "TabGroup";
//#endregion
export { TabGroup };

//# sourceMappingURL=index.js.map